/**
 * <s-mega-panel> — switches the right-hand view of a desktop mega panel.
 *
 * Purely data-driven: every view and trigger is rendered by Liquid from the
 * selected Shopify menu; this component only shows one view at a time. It
 * contains no category names, URLs or product data.
 *
 * Required child markup:
 *   a[data-mega-trigger]  — a left-column link. Its `aria-controls` holds the id
 *                           of the view it reveals. The link still navigates on click.
 *   [data-mega-view]      — a right-column view. Inactive views carry `hidden`, so
 *                           their lazy images are not requested and their links
 *                           cannot be reached with the keyboard.
 *
 * Behaviour:
 *   - Pointer: hovering a trigger activates its view after a short intent delay.
 *     While the pointer moves toward the views column the delay is longer, so a
 *     diagonal path across other triggers never switches the view by accident.
 *     Entering the views column cancels any pending switch.
 *   - Keyboard: focusing a trigger activates its view immediately; Tab then moves
 *     on through the list and into the visible view.
 *   - When the surrounding mega item is left (or focus leaves it), the panel
 *     returns to its default view after the close delay, so it reopens cleanly.
 *
 * Opening/closing the panel itself stays with <s-nav-dropdown> and CSS.
 * Listeners are bound per connection with an AbortController, so moving or
 * re-rendering the element never stacks duplicate handlers.
 */
const INTENT_DELAY = 70;
const AIM_DELAY = 220;
const RESET_DELAY = 420;
const DESKTOP_QUERY = '(min-width: 64rem)';

/**
 * Speed: normal pages ship the mega views empty ([data-mega-pending]); the tiles come from one
 * shared background request to the panel's data-mega-src (a noindex view of the same layout).
 * It starts when the browser is idle after load on desktop, or immediately on first hover/focus.
 * @type {Map<string, Promise<Document | null>>}
 */
const megaSources = new Map();

/** @param {string} url */
function loadMegaSource(url) {
  let request = megaSources.get(url);
  if (!request) {
    request = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch(() => {
        megaSources.delete(url);
        return null;
      });
    megaSources.set(url, request);
  }
  return request;
}

class SMegaPanel extends HTMLElement {
  #ac;
  /** @type {HTMLAnchorElement[]} */
  #triggers = [];
  /** @type {HTMLAnchorElement | null} */
  #active = null;
  #switchTimer = 0;
  #resetTimer = 0;
  #lastX = 0;
  #lastY = 0;
  #movingRight = false;
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {HTMLElement | null} */
  #indicator = null;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#triggers = [...this.querySelectorAll('a[data-mega-trigger]')].filter((trigger) => this.#viewFor(trigger));
    if (this.#triggers.length === 0) return;

    this.#list = this.querySelector('[data-mega-list]');
    this.#indicator = this.querySelector('[data-mega-indicator]');
    this.#active = this.#triggers.find((trigger) => trigger.hasAttribute('data-active')) ?? this.#triggers[0];
    this.#activate(this.#default());

    for (const trigger of this.#triggers) {
      trigger.addEventListener('pointerenter', (event) => this.#onTriggerEnter(event, trigger), opts);
      trigger.addEventListener('pointerleave', () => this.#cancelSwitch(), opts);
      trigger.addEventListener('focus', () => this.#activate(trigger), opts);
    }

    const list = this.#triggers[0].closest('ul');
    list?.addEventListener('pointermove', this.#onPointerMove, opts);

    this.querySelector('[data-mega-view]')?.parentElement?.addEventListener('pointerenter', () => this.#cancelSwitch(), opts);

    const host = this.closest('.nav-has-mega') ?? this;
    host.addEventListener('mouseenter', () => {
      clearTimeout(this.#resetTimer);
      // Re-measure on open: fonts or the viewport may have changed while the panel was closed.
      this.#placeIndicator(false);
    }, opts);
    // Re-measure once web fonts are ready, since the bold weight changes row heights.
    document.fonts?.ready.then(() => this.#placeIndicator(false));
    host.addEventListener('mouseleave', () => this.#scheduleReset(), opts);
    this.#setupFill(host, opts);
    host.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && host.contains(event.relatedTarget)) return;
      this.#scheduleReset();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#switchTimer);
    clearTimeout(this.#resetTimer);
  }

  /**
   * @param {Element} host
   * @param {AddEventListenerOptions} opts
   */
  #setupFill(host, opts) {
    const url = this.dataset.megaSrc;
    if (!url || !this.querySelector('[data-mega-pending]')) return;

    const fill = () => this.#fill(url);
    host.addEventListener('pointerenter', fill, { ...opts, once: true });
    host.addEventListener('focusin', fill, { ...opts, once: true });

    const idle = () => {
      if (opts.signal?.aborted || !window.matchMedia(DESKTOP_QUERY).matches) return;
      if ('requestIdleCallback' in window) window.requestIdleCallback(fill, { timeout: 3000 });
      else window.setTimeout(fill, 1500);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { ...opts, once: true });
  }

  /** @param {string} url */
  async #fill(url) {
    if (!this.querySelector('[data-mega-pending]')) return;
    const source = await loadMegaSource(url);
    if (!source || !this.isConnected) return;
    const here = window.location.pathname;
    for (const view of this.querySelectorAll('[data-mega-pending]')) {
      const filled = source.getElementById(view.id);
      if (!filled) continue;
      view.innerHTML = filled.innerHTML;
      view.removeAttribute('data-mega-pending');
      for (const link of view.querySelectorAll('a.nav-mega-card[href]')) {
        if (new URL(link.href, window.location.href).pathname === here) link.setAttribute('aria-current', 'page');
      }
    }
    this.#placeIndicator(false);
  }

  /** The current page's item if it is in this panel, otherwise the first item. */
  #default() {
    return this.#triggers.find((trigger) => trigger.getAttribute('aria-current') === 'page') ?? this.#triggers[0];
  }

  /** @param {HTMLAnchorElement} trigger */
  #viewFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.querySelector(`[id="${id}"]`) : null;
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const dx = event.clientX - this.#lastX;
    const dy = event.clientY - this.#lastY;
    const towardViews = getComputedStyle(this).direction === 'rtl' ? dx < 0 : dx > 0;
    this.#movingRight = towardViews && Math.abs(dx) > Math.abs(dy) * 0.6;
    this.#lastX = event.clientX;
    this.#lastY = event.clientY;
  };

  /**
   * @param {PointerEvent} event
   * @param {HTMLAnchorElement} trigger
   */
  #onTriggerEnter(event, trigger) {
    if (event.pointerType === 'touch') {
      this.#activate(trigger);
      return;
    }
    this.#cancelSwitch();
    const delay = this.#movingRight ? AIM_DELAY : INTENT_DELAY;
    this.#switchTimer = window.setTimeout(() => this.#activate(trigger), delay);
  }

  #cancelSwitch() {
    clearTimeout(this.#switchTimer);
  }

  #scheduleReset() {
    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      if (this.closest('.nav-has-mega')?.matches(':hover, [data-open]')) return;
      this.#activate(this.#default());
    }, RESET_DELAY);
  }

  /** @param {HTMLAnchorElement} next */
  #activate(next) {
    this.#cancelSwitch();
    if (!next) return;
    for (const trigger of this.#triggers) {
      const isActive = trigger === next;
      trigger.toggleAttribute('data-active', isActive);
      trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      const view = this.#viewFor(trigger);
      if (view && view.hidden === isActive) view.hidden = !isActive;
    }
    this.#active = next;
    this.#placeIndicator(true);
  }

  /**
   * Moves the shared highlight to the active trigger using only a transform
   * (plus its own height), so no neighbouring item is laid out again. The first
   * placement happens without transition; later moves glide. Skipped while the
   * desktop panel is not rendered (offsetHeight 0), then re-measured on open.
   * @param {boolean} animate
   */
  #placeIndicator(animate) {
    const list = this.#list;
    const indicator = this.#indicator;
    const trigger = this.#active;
    if (!list || !indicator || !trigger || trigger.offsetHeight === 0) return;

    const listTop = list.getBoundingClientRect().top;
    const rect = trigger.getBoundingClientRect();
    indicator.style.setProperty('--y', `${Math.round(rect.top - listTop)}px`);
    indicator.style.setProperty('--h', `${Math.round(rect.height)}px`);

    if (!list.hasAttribute('data-indicator-ready')) {
      list.setAttribute('data-indicator-ready', '');
      // Enable gliding only after the first position has been painted.
      requestAnimationFrame(() => requestAnimationFrame(() => list.setAttribute('data-indicator-animate', '')));
      return;
    }
    if (animate && !list.hasAttribute('data-indicator-animate')) list.setAttribute('data-indicator-animate', '');
  }
}

if (!customElements.get('s-mega-panel')) customElements.define('s-mega-panel', SMegaPanel);
